import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoicePart } from '../api/types';
import { type AvatarKind, initials } from '../lib/people';
import styles from './Avatar.module.css';
import { ConductorIcon } from './ConductorIcon';
import { LyreIcon } from './LyreIcon';

interface Props {
  name: string;
  kind?: AvatarKind;
  /** The section this person leads as Grup Lideri: shown as the lyre badge, named with the section. */
  leadsSection?: VoicePart | null;
  size?: 'small' | 'medium' | 'large';
}

/**
 * A person's initials in a circle, used wherever people are listed. The role is a badge at the lower
 * right: the conductor for the Şef, the lyre for a Grup Lideri. The badge carries the role
 * for screen readers and as a hint, so pages do not repeat it in text.
 */
export function Avatar({ name, kind = 'member', leadsSection = null, size = 'medium' }: Props) {
  const { t } = useTranslation();
  const director = kind === 'director';
  const badgeLabel = director
    ? t('roles.DIRECTOR')
    : leadsSection
      ? t('avatar.leads', { role: t('roles.SECTION_LEADER'), part: t(`parts.${leadsSection}`) })
      : null;
  return (
    <span className={`${styles.avatar} ${styles[kind]} ${styles[size]}`} data-avatar={kind} data-leads={leadsSection ?? undefined}>
      <span aria-hidden="true">{initials(name)}</span>
      {badgeLabel && (
        <span className={`${styles.badge} ${director ? styles.directorBadge : ''}`} role="img" aria-label={badgeLabel} title={badgeLabel}>
          {director ? <ConductorIcon className={styles.conductor} /> : <LyreIcon className={styles.lyre} />}
        </span>
      )}
    </span>
  );
}

/** An avatar followed by a name (and anything else), aligned on one line. */
export function Person({ children, ...avatar }: Props & { children: ReactNode }) {
  return (
    <span className={styles.person}>
      <Avatar {...avatar} />
      <span className={styles.personText}>{children}</span>
    </span>
  );
}
